import clsx from 'clsx';

// Hooks
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { deleteApi } from '@/utils/client';
import { BASE } from '@/types/utils/sizesType';

// Types
import type { SocialProfileNetworkType, SocialProfileType } from '@/types/socialProfileType';

// Endpoint
import { USER_SOCIAL_PROFILES } from '@/lib/routes/apiEndpoints';
// lib
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    TIKTOK_BUSINESS,
    TWITTER,
    YOUTUBE
} from '@/lib/socialProfile';

import AvatarCompound, { DefaultAvatar as Avatar } from '@/components/base/Avatar';
// Libs
import { iconSize } from '@/components/base/Avatar/avatarConfig';
// Components
import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';
import TooltipAvatarContent from '@/components/base/Tooltip/TooltipAvatarContent';
import Instagram from '@/components/icons/Instagram';
import { PinterestIcon } from '@/components/icons/Networks/PinterestIcon';
import { YoutubeIcon } from '@/components/icons/Networks/YoutubeIcon';

type NetworkTypeLabelProps = { socialNetworkType: SocialProfileNetworkType };

const NetworkTypeLabel = ({ socialNetworkType }: NetworkTypeLabelProps) => {
    const networkTypeIcon = () => {
        switch (socialNetworkType) {
            case INSTAGRAM:
                return <Instagram aria-label="instagram" />;

            case YOUTUBE:
                return <YoutubeIcon aria-label="youtube" />;

            case PINTEREST:
                return <PinterestIcon aria-label="pinterest" />;

            default:
                return (
                    <Icon
                        icon={socialNetworkType}
                        className={clsx('text-8', iconSize[BASE], {
                            'text-facebook': socialNetworkType === FACEBOOK,
                            'text-linkedin': socialNetworkType === LINKEDIN,
                            'text-twitter': socialNetworkType === TWITTER,
                            'text-tiktok': socialNetworkType === TIKTOK_BUSINESS
                        })}
                    />
                );
        }
    };

    return (
        <span className="flex max-w-fit flex-row items-center space-x-0.5 rounded-full border border-icoGray-300 px-1.5 py-1">
            {networkTypeIcon()}
            <span className="text-8 uppercase">
                {socialNetworkType === TIKTOK_BUSINESS ? 'tiktok' : socialNetworkType}
            </span>
        </span>
    );
};

type SocialProfilesConnectedListProps = {
    onDeleteError: () => void;
};

const SocialProfilesConnectedList = ({ onDeleteError }: SocialProfilesConnectedListProps) => {
    const { t } = useTranslation('commons');
    const { socialProfiles, mutateSocialProfiles } = useSocialProfiles();
    const socialProfilesByTypes = socialProfiles?.reduce(
        (acc, sp) => ({
            ...acc,
            [sp.social_network_type]: acc[sp.social_network_type]
                ? [...acc[sp.social_network_type], sp]
                : [sp]
        }),
        {} as Record<SocialProfileNetworkType, SocialProfileType[]>
    );

    const deleteSocialProfile = async (idSocialProfile: SocialProfileType['id']) => {
        try {
            const listSocialProfilesUpdated = socialProfiles.filter(
                (profile) => profile?.id !== idSocialProfile
            );
            mutateSocialProfiles(listSocialProfilesUpdated, false);
            await deleteApi(`${USER_SOCIAL_PROFILES}/${idSocialProfile}`);
        } catch (e) {
            mutateSocialProfiles(socialProfiles, false);
            onDeleteError();
        }
    };

    return (
        <div className="flex flex-col space-y-5">
            {Object.keys(socialProfilesByTypes).map((socialNetworkType) => (
                <div className="rounded-md bg-white p-3" key={socialNetworkType}>
                    <div className="flex flex-row items-center justify-between">
                        <NetworkTypeLabel socialNetworkType={socialNetworkType} />
                        <span className="flex flex-row items-center space-x-1 rounded-full border border-icoGray-300 px-1.5 py-1 text-8">
                            <Icon icon="single-profile" className="text-8" />
                            <span>{socialProfilesByTypes[socialNetworkType].length}</span>
                            <Icon icon="check" className="text-icoGreen" />
                        </span>
                    </div>
                    <div className="flex w-full flex-row flex-wrap" role="list">
                        {socialProfilesByTypes[socialNetworkType].map((profile) => (
                            <Tooltip
                                key={profile.id}
                                position="bottom"
                                content={<TooltipAvatarContent slug={profile?.slug} />}
                                arrow={false}
                                variant="avatar">
                                <span className="mr-3 mt-3">
                                    <Avatar
                                        role="listitem"
                                        size="base"
                                        url={profile?.external_api_data?.avatar}
                                        fallback={profile?.slug?.[0]}
                                        profileType={profile?.social_network_type}
                                        onHover={
                                            <AvatarCompound.HoverButton
                                                aria-label={t('delete')}
                                                onClick={() => deleteSocialProfile(profile?.id)}
                                                className="rounded-full bg-icoBlack/80">
                                                <Icon icon="trash" className="text-white" />
                                            </AvatarCompound.HoverButton>
                                        }
                                    />
                                </span>
                            </Tooltip>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default SocialProfilesConnectedList;
